import React from 'react';
import styled from '@emotion/styled';
import type { SpaceProps } from 'styled-system';
import { space } from 'styled-system';

import { DisclaimerContent } from 'common/components/Coupon/DisclaimerContent';
import { useCampaignBannerDrawer } from 'common/hooks/useCampaignBannerDrawer';
import { InfoDrawerContent } from '../../../../banner-voucher-v2/components/shared/InfoDrawer';
import { MessageType } from '../../../enums/Alert';
import { useOpenDrawer } from '../../../hooks/drawer';
import { useLocalization } from '../../../providers/LocaleProvider';
import type { CampaignBadge as CampaignBadgeData } from '../../../types/product-card';
import Alert from '../../Alert';
import { Badge } from '../../Badge';
import { Flex } from '../../Flex';
import { CenteredSpinner } from '../../Spinner';

type CampaignBadgeProps = CampaignBadgeData & { sku: string };

type CampaignBannerData = NonNullable<
  NonNullable<ReturnType<typeof useCampaignBannerDrawer>['campaign']>['campaignBanner']
>;

const Wrapper = styled.div<SpaceProps>`
  ${space};
`;

const wrapperStyles: SpaceProps = {
  mb: ['sp_8', null, null, 'sp_12'],
};

const VoucherDrawerContent = ({
  campaignBanner,
  isHomeClubMember,
}: {
  campaignBanner: CampaignBannerData;
  isHomeClubMember: boolean;
}) => {
  const gqlCampaignType = campaignBanner.campaignType;

  const voucherCode = gqlCampaignType?.__typename === 'Voucher' ? gqlCampaignType.code : null;

  return (
    <InfoDrawerContent
      shortName={campaignBanner.shortName ?? ''}
      name={campaignBanner.name}
      voucherCode={voucherCode}
      onlyHomeClub={gqlCampaignType?.conditions?.onlyHomeClub ?? false}
      isHomeClubMember={isHomeClubMember}
      countdownEndDate={campaignBanner.countdownEndDate ?? ''}
      campaignTextColor="sys.neutral.text.default"
      campaignBackgroundColor="sys.primary.background.weak"
      legalDisclaimerContent={campaignBanner.legalDisclaimerContent ?? ''}
      extraDisclaimerContent={campaignBanner.extraDisclaimerContent ?? ''}
    />
  );
};

const ErrorAlert = () => {
  const { t } = useLocalization();

  return (
    <Flex p="sp_16">
      <Alert type={MessageType.Error}>{t('h24_something_went_wrong')}</Alert>
    </Flex>
  );
};

export const CampaignBadgeDrawerContent = ({
  sku,
  campaignKey,
}: Pick<CampaignBadgeProps, 'sku' | 'campaignKey'>) => {
  const { campaign, isHomeClubMember, loading, hasError } = useCampaignBannerDrawer(
    sku,
    campaignKey
  );

  if (loading) return <CenteredSpinner />;
  if (hasError || !campaign?.campaignBanner) return <ErrorAlert />;

  const { campaignBanner } = campaign;
  const fetchedType = campaignBanner.campaignType?.__typename;

  if (fetchedType === 'OnlineCoupon') {
    return (
      <DisclaimerContent
        hasHomeclubCondition={campaignBanner.campaignType?.conditions?.onlyHomeClub ?? false}
        legalDisclaimerContent={campaignBanner.legalDisclaimerContent}
      />
    );
  }

  if (fetchedType === 'Voucher') {
    return (
      <VoucherDrawerContent campaignBanner={campaignBanner} isHomeClubMember={isHomeClubMember} />
    );
  }

  return <ErrorAlert />;
};

export const CampaignBadge = ({
  sku,
  priceAfterDiscount,
  campaignKey,
  onlyHomeClub,
}: CampaignBadgeProps) => {
  const { fc, t, r } = useLocalization();

  const openDrawer = useOpenDrawer(
    {
      title: t('h24_important_info'),
      content: <CampaignBadgeDrawerContent sku={sku} campaignKey={campaignKey} />,
    },
    { preventDefault: true, stopPropagation: true }
  );

  return (
    <Wrapper {...wrapperStyles}>
      <Badge
        iconName="info"
        onIconClick={openDrawer}
        iconAriaLabel={t('h24_info_icon')}
        textColor="sys.marketing.orange"
        backgroundColor="sys.primary.background.weak"
      >
        <span
          dangerouslySetInnerHTML={{
            __html: r.t('h24_only_price', { finalPrice: fc(priceAfterDiscount) }),
          }}
        />{' '}
        {onlyHomeClub && t('h24_part_of_homeclub')}
      </Badge>
    </Wrapper>
  );
};
